class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');

    form.addEventListener('submit', this.onFormSubmit.bind(this));
    form.addEventListener('reset', this.onFormReset.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length == 0) {
      this.predictiveSearchResults.style.opacity = 0;
    } else {
      this.getSearchResults(searchTerm);
      this.predictiveSearchResults.style.opacity = 1;
    }

    if (!searchTerm.length) {
      this.close(true);
      return;
    }
  }

  onFormSubmit(event) {
    // Prevenir submit apenas se:
    // 1. Não houver query (busca vazia)
    // 2. Ou se houver um item selecionado (para permitir navegação por teclado)
    const hasQuery = this.getQuery().length > 0;
    const hasSelectedItem = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    
    if (!hasQuery || hasSelectedItem) {
      event.preventDefault();
    }
    // Se houver query e não houver item selecionado, permitir o submit normal
  }

  onFormReset(event) {
    this.close(true);
    this.input.focus();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!this.classList.contains('focus-form')) {
      this.classList.add('focus-form');
    }

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    if (this.dataset.persist === 'true') {
      return;
    }

    const searchTerm = this.getQuery();

    if (searchTerm.length == 0) {
      this.classList.remove('focus-form');
    }

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        // Se houver um item selecionado, clicar nele
        // Caso contrário, permitir o submit padrão do formulário
        const selectedItem = this.querySelector('[aria-selected="true"]');
        if (selectedItem) {
          event.preventDefault();
          this.selectOption();
        }
        // Se não houver item selecionado, deixar o formulário submeter normalmente
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    let searchTerm = this.getQuery();
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);

    if (searchTerm.length != 0) {
      this.setAttribute('loading', true);
    }
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    
    const existingResults = this.querySelector('.predictive-search-results');
    if (existingResults) {
      existingResults.remove();
    }
    
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // ✅ Fix: Garantir que todas as URLs de imagens sejam absolutas
    // Isso resolve o problema de imagens não carregarem em outras rotas (collections, etc)
    this.fixImageUrls();

    let container = this.querySelector('.predictive-search-results');
    let results_products = this.querySelectorAll('.predictive-search__list-item');
    let button = this.querySelector('.predictive-search__button');

    for (let i = 0; i < results_products.length; i++) {
      results_products[i].classList.add('animate--fade-in-up');
      results_products[i].style.animationDelay = '' + i + '00ms';
    }

    if (button) {
      button.classList.add('animate--fade-in-up');
      button.style.animationDelay = '' + results_products.length + '00ms';
    }

    setTimeout(this.open(), 600);

    this.open();
    this.setLiveRegionResults();
  }

  fixImageUrls() {
    // Converter todas as URLs de imagens relativas em absolutas
    // Isso resolve o problema de imagens não carregarem em outras rotas (collections, etc)
    const images = this.querySelectorAll('img');
    const shopUrl = window.shopUrl || window.location.origin;
    const protocol = window.location.protocol;
    
    images.forEach((img) => {
      // Corrigir src - verificar o atributo src original, não a propriedade (que já pode estar resolvida)
      const srcAttr = img.getAttribute('src');
      if (srcAttr) {
        // Se começar com //, converter para protocolo absoluto
        if (srcAttr.startsWith('//')) {
          img.setAttribute('src', protocol + srcAttr);
        }
        // Se for relativa (não começa com http ou /), converter para absoluta
        else if (!srcAttr.startsWith('http') && !srcAttr.startsWith('/')) {
          img.setAttribute('src', shopUrl + '/' + srcAttr);
        }
        // Se começar com /, garantir que seja absoluta
        else if (srcAttr.startsWith('/') && !srcAttr.startsWith('//')) {
          img.setAttribute('src', shopUrl + srcAttr);
        }
      }
      
      // Corrigir srcset
      const srcsetAttr = img.getAttribute('srcset');
      if (srcsetAttr) {
        const srcsetParts = srcsetAttr.split(',');
        const fixedSrcset = srcsetParts.map(part => {
          const trimmed = part.trim();
          const parts = trimmed.split(/\s+/);
          const url = parts[0];
          const descriptor = parts.slice(1).join(' ');
          
          if (url) {
            let absoluteUrl = url;
            
            // Se começar com //, converter para protocolo absoluto
            if (url.startsWith('//')) {
              absoluteUrl = protocol + url;
            }
            // Se for relativa, converter para absoluta
            else if (!url.startsWith('http') && !url.startsWith('/')) {
              absoluteUrl = shopUrl + '/' + url;
            }
            // Se começar com /, garantir que seja absoluta
            else if (url.startsWith('/') && !url.startsWith('//')) {
              absoluteUrl = shopUrl + url;
            }
            
            return descriptor ? `${absoluteUrl} ${descriptor}` : absoluteUrl;
          }
          return trimmed;
        }).join(', ');
        
        img.setAttribute('srcset', fixedSrcset);
      }
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    let searchTerm = this.getQuery();

    if (this.querySelector('.predictive-search-results') && searchTerm.length == 0) {
      this.querySelector('.predictive-search-results').remove();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
